/**
 * Utility functions for managing campaign wizard state in localStorage/cookies
 */

const STORAGE_KEY_PREFIX = 'campaign_draft'

export interface CampaignWizardState {
  step1?: {
    name?: string
    campaign_type?: 'broadcast' | 'targeted'
    channel_id?: string
    description?: string
  }
  step2?: {
    template_id?: string
    variable_mappings?: Array<{
      csv_column: string
      template_var: string
      user_label?: string
    }>
  }
  step3?: {
    audience_type?: 'upload' | 'master'
    csv_file?: File | null
    csv_filename?: string
    csv_columns?: string[]
    csv_preview?: any[]
    total_rows?: number
    column_mappings?: Record<string, string>
  }
  step4?: {
    start_at?: string
    end_at?: string
    timezone?: string
    send_window_start?: string
    send_window_end?: string
  }
}

export const campaignStorage = {
  /**
   * Get storage key for current SMB
   */
  getStorageKey: (smbId: string): string => {
    return `${STORAGE_KEY_PREFIX}_${smbId}`
  },

  /**
   * Save wizard state
   */
  save: (smbId: string, state: CampaignWizardState): void => {
    try {
      const key = campaignStorage.getStorageKey(smbId)
      // Note: File objects cannot be serialized, so we skip csv_file
      const serializableState = {
        ...state,
        step3: state.step3
          ? {
              ...state.step3,
              csv_file: null, // Don't store file in localStorage
            }
          : undefined,
      }
      localStorage.setItem(key, JSON.stringify(serializableState))
    } catch (error) {
      console.error('Failed to save campaign wizard state:', error)
    }
  },

  /**
   * Load wizard state
   */
  load: (smbId: string): CampaignWizardState | null => {
    try {
      const key = campaignStorage.getStorageKey(smbId)
      const stored = localStorage.getItem(key)
      if (stored) {
        return JSON.parse(stored) as CampaignWizardState
      }
    } catch (error) {
      console.error('Failed to load campaign wizard state:', error)
    }
    return null
  },

  /**
   * Clear wizard state
   */
  clear: (smbId: string): void => {
    try {
      const key = campaignStorage.getStorageKey(smbId)
      localStorage.removeItem(key)
    } catch (error) {
      console.error('Failed to clear campaign wizard state:', error)
    }
  },

  /**
   * Update specific step data
   */
  updateStep: (smbId: string, step: keyof CampaignWizardState, data: any): void => {
    const current = campaignStorage.load(smbId) || {}
    campaignStorage.save(smbId, {
      ...current,
      [step]: data,
    })
  },
}

